import { db } from "../lib/firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

// ---------------------------------------------------------------------------
// Generic CRUD
// ---------------------------------------------------------------------------

/**
 * Get all documents from a collection.
 * @param {string} colName - Firestore collection name.
 * @param {Array} [fallback=[]] - Optional seed data to return on error.
 * @returns {Promise<Array>} Array of documents.
 */
export const getAll = async (colName, fallback = []) => {
  try {
    const q = query(collection(db, colName), orderBy("createdAt", "asc"));
    const snapshot = await getDocs(q);
    if (snapshot.empty && fallback.length > 0) return fallback;
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error(`Error fetching ${colName}:`, error);
    return fallback;
  }
};

/**
 * Get a single document by ID.
 * If not found, returns null.
 */
export const getById = async (colName, id) => {
  try {
    const ref = doc(db, colName, id);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error(`Error fetching ${colName}/${id}:`, error);
    return null;
  }
};

/**
 * Create a new document. If `id` is provided, use setDoc (with that id);
 * otherwise use addDoc (auto-generated id).
 */
export const create = async (colName, data, id = null) => {
  try {
    const payload = { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
    if (id) {
      await setDoc(doc(db, colName, id), payload);
      return { id, ...payload };
    }
    const ref = await addDoc(collection(db, colName), payload);
    return { id: ref.id, ...payload };
  } catch (error) {
    console.error(`Error creating document in ${colName}:`, error);
    throw error;
  }
};

/**
 * Update an existing document.
 */
export const update = async (colName, id, data) => {
  try {
    const ref = doc(db, colName, id);
    await updateDoc(ref, { ...data, updatedAt: serverTimestamp() });
    return { id, ...data };
  } catch (error) {
    console.error(`Error updating ${colName}/${id}:`, error);
    throw error;
  }
};

/**
 * Delete a document.
 */
export const remove = async (colName, id) => {
  try {
    await deleteDoc(doc(db, colName, id));
    return true;
  } catch (error) {
    console.error(`Error deleting ${colName}/${id}:`, error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Settings (singleton document)
// ---------------------------------------------------------------------------

const SETTINGS_ID = "main";

/**
 * Get site settings.
 */
export const getSettings = async () => {
  try {
    const ref = doc(db, "settings", SETTINGS_ID);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error fetching settings:", error);
    return null;
  }
};

/**
 * Update site settings.
 */
export const updateSettings = async (data) => {
  try {
    const ref = doc(db, "settings", SETTINGS_ID);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
    return { id: SETTINGS_ID, ...data };
  } catch (error) {
    console.error("Error updating settings:", error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// Seed / migration helpers
// ---------------------------------------------------------------------------

/**
 * Seed a collection with initial data. Skips documents that already exist
 * if the document id matches an existing one.
 */
export const seedCollection = async (colName, dataArray, idField = "id") => {
  const results = { created: 0, skipped: 0, errors: 0 };

  for (const item of dataArray) {
    try {
      const docId = item[idField];
      if (docId) {
        const ref = doc(db, colName, docId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          results.skipped++;
          continue;
        }
        await setDoc(ref, {
          ...item,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, colName), {
          ...item,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      results.created++;
    } catch (error) {
      console.error(`Error seeding ${colName}:`, error);
      results.errors++;
    }
  }

  return results;
};

/**
 * Batch seed a collection — faster for large datasets.
 */
export const seedCollectionBatch = async (colName, dataArray, idField = "id") => {
  const batch = writeBatch(db);
  let created = 0;
  let skipped = 0;

  for (const item of dataArray) {
    const docId = item[idField];
    if (docId) {
      const ref = doc(db, colName, docId);
      batch.set(ref, {
        ...item,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      created++;
    } else {
      const ref = doc(collection(db, colName));
      batch.set(ref, {
        ...item,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      created++;
    }
  }

  await batch.commit();
  return { created, skipped };
};
